import { rankOf, suitOf } from "./cards";
import { handPct, rangedEquity, texture, drawInfo } from "./aiProPlus";
import { profile, handRange, PRIORS } from "./opponentModel";

/*
 * PRO++ — adaptive exploitative profile (runs in the browser, public info only).
 *
 * How the stats layer feeds decisions:
 *  1. opponentModel.js keeps decayed, Bayesian-smoothed stats per player (VPIP/PFR/3-bet/fold-to-3-bet,
 *     steal/fold-to-steal, AF, fold-to-cbet/bet/raise by street, continue % by bet size, WTSD,
 *     bluffs shown) and a per-hand range estimate narrowed by that player's own tendencies.
 *  2. readTable() turns those into a few knobs for the current spot. Each knob is a deviation from
 *     the population prior × sample confidence × CFG.exploitWeight, clamped to ±CFG.maxDeviation.
 *     That clamp is the "balanced floor": reads move frequencies, they never flip the strategy.
 *        foldEquity()  chance everyone still in folds to a bet/raise of a given size
 *        sticky        how much more than normal they call (calling stations)
 *        bluffy        how often the current aggressor's bets are bluffs (maniacs vs nits)
 *        stealDev      how much the blinds over-fold to steals
 *  3. BASELINE is a solid tight-aggressive strategy. With exploitWeight = 0 every knob is neutral
 *     and Pro++ plays that baseline.
 *  4. Equity is always computed against the modelled ranges, so loose players are automatically
 *     called and value-bet wider, and tight ones are respected.
 *  Mixed strategies: most choices use a probability, which is returned as `freq` on the action.
 */

export const CFG = {
  exploitWeight: 1.0,
  maxDeviation: 0.3,
  iters: { pre: 450, post: 650 },
};

// Baseline (exploit-free) parameters: tune these to change the core strategy
export const BASELINE = {
  open: { EP: 0.14, MP: 0.19, CO: 0.27, BTN: 0.44, SB: 0.4, BB: 0 },
  push12: { EP: 0.11, MP: 0.14, CO: 0.19, BTN: 0.27, SB: 0.36, BB: 0.3 },
  huOpenSB: 0.8,
  bluff3bet: { late: 0.38, other: 0.18 },
  bluff4bet: 0.3,
  valueThr: 0.72, thinThr: 0.5, raiseThr: 0.78,
  slowplay: { ip: 0.12, oop: 0.25 },
  semiBluff: 0.6, cbetAir: { dry: 0.62, wet: 0.38 }, stab: 0.2, riverBluff: 0.3, float: 0.3,
};

const rnd = () => Math.random();
const clamp = (x, a, b) => Math.max(a, Math.min(b, x));
const mean = (a) => (a.length ? a.reduce((x, y) => x + y, 0) / a.length : 0);
const shift = (x) => clamp(x * CFG.exploitWeight, -CFG.maxDeviation, CFG.maxDeviation);

const check = { type: "check" };
const passive = (v) => (v.toCall === 0 ? check : { type: "call" });
const fold = (v) => (v.toCall === 0 ? check : { type: "fold" });
const allIn = (v) => (v.canRaise ? { type: "raise", amount: v.maxTo } : passive(v));
function raiseTo(v, amt) {
  if (!v.canRaise) return passive(v);
  let to = Math.max(v.minRaiseTo, Math.round(amt / v.sbUnit) * v.sbUnit);
  if (to >= v.maxTo * 0.7) to = v.maxTo;
  return { type: "raise", amount: Math.min(to, v.maxTo) };
}
// Mixed strategy: take `action` with probability p, else `other`; the chosen frequency is reported
const mix = (p, action, other) => {
  const f = clamp(p, 0, 1);
  return rnd() < f ? { ...action, freq: f } : { ...other, freq: 1 - f };
};

// ---------- reads: stats -> exploit knobs ----------
const dev = (o, key) => (o.prof.est[key] - PRIORS[key][0]) * (0.5 + 0.5 * o.prof.conf);
const sticky = (o) => dev(o, "cont_large") + dev(o, "weakCall") * 0.5 + dev(o, "wtsd") * 0.7 - dev(o, "foldToBet_flop") * 0.5;
const bluffy = (o) => (o ? dev(o, "bluffShown") + dev(o, "aggFreq") * 0.6 + (o.prof.af - 1.5) * 0.05 * o.prof.conf : 0);

function readTable(v) {
  const opps = v.opponents.map((o) => ({ ...o, prof: profile(v.model, o.id), range: handRange(v.model, o.id) }));
  const active = opps.filter((o) => !o.allIn);
  const raiser = opps.find((o) => o.id === v.raiserId) || null;
  const blinds = active.filter((o) => o.pos === "SB" || o.pos === "BB");
  return {
    opps, active, raiser,
    sticky: shift(mean(active.map(sticky))),
    bluffy: shift(bluffy(raiser)),
    stealDev: shift(mean(blinds.map((o) => dev(o, "foldToSteal")))),
    looseDev: shift(mean(active.map((o) => dev(o, "vpip")))),
  };
}

// Probability every active opponent folds to a bet/raise of `frac` × pot, and the population baseline
function foldEquity(v, t, frac, raising) {
  if (!t.active.length) return { fe: 0, base: 0 };
  const bucket = frac < 0.45 ? "small" : frac < 0.8 ? "medium" : "large";
  const cbet = !raising && v.wasAggressor;
  const key = raising ? `foldToRaise_${v.street}` : cbet ? `foldToCbet_${v.street}` : `foldToBet_${v.street}`;
  const ck = `cont_${bucket}`;
  const b = 0.5 * PRIORS[key][0] + 0.5 * (1 - PRIORS[ck][0]);
  let fe = 1, base = 1;
  t.active.forEach((o) => {
    const m = 0.5 * o.prof.est[key] + 0.5 * (1 - o.prof.est[ck]);
    fe *= clamp(b + shift((m - b) * (0.5 + 0.5 * o.prof.conf)), 0.02, 0.95);
    base *= b;
  });
  return { fe, base };
}

// Bluff frequency: baseline × read multiplier, gated by a simple EV check on fold equity
function bluffFreq(baseFreq, fe, feBase, frac) {
  const breakeven = frac / (1 + frac);
  let f = baseFreq * clamp(1 + (fe - feBase) * 3 * CFG.exploitWeight, 0.35, 2);
  if (fe > breakeven + 0.12) f = Math.max(f, 0.55);
  if (fe < breakeven - 0.15) f = Math.min(f, 0.08);
  return clamp(f, 0, 0.9);
}

// ---------- preflop ----------
function handShape(hole) {
  const [a, b] = hole;
  const r1 = Math.max(rankOf(a), rankOf(b)), r2 = Math.min(rankOf(a), rankOf(b));
  const suited = suitOf(a) === suitOf(b);
  const blocker = (suited && r1 === 14 && r2 <= 5) || (suited && r1 - r2 <= 2 && r2 >= 5) || (r1 === 14 && r2 >= 9) || (suited && r1 === 13 && r2 >= 9);
  return { r1, r2, suited, pair: r1 === r2, blocker };
}

function callOff(v, rr, odds) {
  const behind = Math.max(0, v.nOpp - 1);
  const eq = rangedEquity(v.hole, [], [rr], CFG.iters.pre);
  const edge = eq - odds - 0.02 - behind * 0.04;
  if (edge > 0.02) return allIn(v);
  if (edge > -0.02) return mix(0.5, allIn(v), fold(v));
  return fold(v);
}

function preflop(v, t) {
  const hp = handPct(v.hole);
  const sh = handShape(v.hole);
  const hu = v.tableSize === 2;
  const eff = v.effBB;
  const odds = v.toCall / (v.pot + v.toCall);
  const late = v.pos === "BTN" || v.pos === "CO";
  const rr = t.raiser ? t.raiser.range : 0.3;
  const B = BASELINE;

  if (eff <= 12) {
    if (v.raiseCount === 0) {
      const base = hu ? (v.pos === "SB" ? 0.6 : 0.45) : B.push12[v.pos];
      const pushPct = Math.min(1, base * (12 / Math.max(eff, 1.5)) ** 0.85 * (1 + Math.max(0, t.stealDev) * 1.5));
      if (v.toCall === 0) return hp <= pushPct * 0.6 ? allIn(v) : check;
      return hp <= pushPct ? allIn(v) : fold(v);
    }
    if (hp <= Math.max(0.02, rr * 0.15)) return allIn(v);
    return callOff(v, rr, odds);
  }
  if (v.raiseCount >= 1 && v.toCall >= v.stack * 0.4) return hp <= 0.02 ? allIn(v) : callOff(v, rr, odds);

  const openBB = eff > 40 ? (v.pos === "SB" ? 3 : late ? 2.3 : 2.5) : eff > 20 ? 2.2 : 2;

  if (v.raiseCount === 0) {
    if (v.toCall === 0) {
      if (hp <= 0.12) return raiseTo(v, v.bb * (3.5 + v.limpers));
      if (v.limpers > 0 && sh.blocker) return mix(0.25 * (1 - t.looseDev), raiseTo(v, v.bb * (3.5 + v.limpers)), check);
      return check;
    }
    // Widen steals vs blinds that over-fold, tighten vs blinds that defend a lot
    let pct = hu ? (v.pos === "SB" ? B.huOpenSB : 0) : B.open[v.pos];
    if (late || v.pos === "SB" || hu) pct *= 1 + Math.max(0, t.stealDev) * 2;
    if (v.limpers > 0) pct *= 0.7 * (1 + t.looseDev);
    if (hp <= pct) {
      if (eff <= 18 && hp > pct * 0.6 && (sh.pair || sh.r1 === 14)) return allIn(v);
      return raiseTo(v, v.bb * (openBB + v.limpers * (1 + Math.max(0, t.looseDev))));
    }
    if (v.pos === "SB" && !hu && v.limpers === 0 && hp <= 0.6) return mix(0.25, passive(v), fold(v));
    if (v.limpers > 0 && v.pos === "BTN" && hp <= 0.45 && (sh.suited || sh.pair)) return passive(v);
    return fold(v);
  }

  const big = v.currentBet / v.bb > 4.5;
  const r = t.raiser;
  if (v.raiseCount === 1) {
    const f3 = r ? shift(dev(r, "foldTo3bet") * 2) : 0;
    let valuePct = Math.max(0.025, rr * 0.22) * (big ? 0.85 : 1) * (1 + Math.max(0, -f3));
    let callPct;
    if (v.pos === "BB") callPct = Math.min(0.65, rr * (1.0 + (0.4 - odds) * 2.5));
    else if (v.pos === "SB") callPct = rr * 0.35;
    else callPct = rr * (late ? 0.7 : 0.45);
    if (big) callPct *= 0.7;
    const jam3 = eff <= 25;
    if (jam3) { callPct *= 0.6; valuePct *= 1.4; }
    const threeBet = () => (jam3 ? allIn(v) : raiseTo(v, v.currentBet * (late ? 3 : 3.8) * (f3 < 0 ? 0.9 : 1)));
    if (hp <= valuePct) return late && !jam3 ? mix(0.8, threeBet(), passive(v)) : threeBet();
    if (hp > callPct && hp <= callPct + 0.12 && sh.blocker) {
      const base = late || v.pos === "BB" || v.pos === "SB" ? B.bluff3bet.late : B.bluff3bet.other;
      return mix(base * clamp(1 + f3 * 1.5, 0.4, 1.8), threeBet(), fold(v));
    }
    if (hp <= callPct) return hp <= valuePct * 1.8 ? mix(0.25, threeBet(), passive(v)) : passive(v);
    return fold(v);
  }

  if (v.raiseCount === 2) {
    // rr already reflects how wide this player really 3-bets; fewer 4-bet bluffs vs players who rarely fold
    const value4 = Math.max(0.025, rr * 0.3);
    let callPct = rr * (v.inPosition ? 0.9 : 0.6) * (eff > 50 ? 1 : 0.6);
    if (odds < 0.3) callPct *= 1.3;
    if (hp <= value4) return eff <= 45 ? allIn(v) : mix(0.4, allIn(v), raiseTo(v, v.currentBet * 2.3));
    if (sh.suited && sh.r1 === 14 && sh.r2 <= 5 && eff > 45) {
      const stub = r ? shift(-bluffy(r)) : 0;
      return mix(B.bluff4bet * clamp(1 + stub, 0.4, 1.5), raiseTo(v, v.currentBet * 2.3), hp <= callPct ? passive(v) : fold(v));
    }
    return hp <= callPct ? passive(v) : fold(v);
  }

  const jam = Math.max(0.02, rr * 0.6);
  if (hp <= jam) return allIn(v);
  if (hp <= jam * 1.6 && odds < 0.35) return passive(v);
  return fold(v);
}

// ---------- postflop ----------
function postflop(v, t) {
  const B = BASELINE;
  const eq = rangedEquity(v.hole, v.board, t.opps.map((o) => o.range), CFG.iters.post);
  const tex = texture(v.board);
  const { flush, oesd } = drawInfo(v.hole, v.board);
  const river = v.street === "river";
  const hasDraw = !river && (flush || oesd);
  const hu = v.nOpp === 1;
  const multi = 1 / Math.max(1, v.nOpp);
  const odds = v.toCall / (v.pot + v.toCall);
  const spr = v.stack / Math.max(1, v.pot);
  const baseSize = tex > 0.5 ? 0.66 + rnd() * 0.1 : 0.33 + rnd() * 0.1;
  const bet = (f) => raiseTo(v, v.pot * f);
  const st = t.sticky;

  if (v.toCall === 0) {
    const valueThr = B.valueThr - st * 0.3 + (v.nOpp - 1) * 0.03;
    const thinThr = B.thinThr - st * 0.25 + (v.nOpp - 1) * 0.05;
    if (eq > valueThr) {
      // Bigger value bets vs stations, fewer slow-plays because they pay off anyway
      const vSize = baseSize * (1 + Math.max(0, st) * 0.8) + (spr < 2 ? 0.3 : 0);
      if (!river) {
        const sp = (v.inPosition ? B.slowplay.ip : B.slowplay.oop) * (tex < 0.4 ? 1.3 : 0.6) * clamp(1 - st * 2, 0.3, 1.5);
        return mix(1 - sp, bet(vSize), check);
      }
      return mix(clamp(0.3 + st, 0.1, 0.6), bet(1.25), bet(0.8));
    }
    if (eq > thinThr) {
      // Thin value: small size, more often vs callers
      if (river) return v.inPosition ? mix(clamp(0.3 + st * 1.5, 0.1, 0.7), bet(0.33), check) : check;
      if (v.wasAggressor || v.inPosition) return mix((0.5 * multi + 0.1) * clamp(1 + st * 2, 0.5, 1.8), bet(0.33), check);
      return check;
    }
    const bluffSize = river ? 0.8 : Math.max(baseSize, hasDraw ? 0.5 : baseSize);
    const { fe, base } = foldEquity(v, t, bluffSize, false);
    const bigger = clamp(1 + (fe - base) * 2 * CFG.exploitWeight, 0.8, 1.35);
    if (hasDraw) return mix(bluffFreq(B.semiBluff * (v.wasAggressor ? 1 : 0.65) * (hu ? 1 : 0.5), fe, base, bluffSize), bet(bluffSize * bigger), check);
    if (river) {
      if (!(hu && (v.wasAggressor || v.inPosition) && eq < 0.25)) return check;
      return mix(bluffFreq(B.riverBluff, fe, base, bluffSize), bet(bluffSize * bigger), check);
    }
    if (v.wasAggressor && v.street === "flop" && v.nOpp <= 2) {
      const c = (tex < 0.4 ? B.cbetAir.dry : B.cbetAir.wet) * (hu ? 1 : 0.5);
      return mix(bluffFreq(c, fe, base, bluffSize), bet(0.33 * bigger), check);
    }
    if (v.inPosition && hu) return mix(bluffFreq(B.stab, fe, base, 0.5), bet(0.5 * bigger), check);
    return check;
  }

  const betRatio = Math.min(1.5, v.toCall / Math.max(1, v.pot - v.toCall));
  const raised = v.raiseCount >= 2;
  const adj = eq - 0.03 * betRatio - (raised ? 0.06 : 0);
  // Call lighter vs players who bluff a lot, tighter vs those who only bet the nuts
  const need = odds - (v.inPosition ? 0.04 : 0.02) - (river ? 0.02 : 0) - t.bluffy * 0.5;
  const commit = v.toCall >= v.stack * 0.45;
  const raiseFrac = 0.8 * (1 + Math.max(0, st) * 0.5);
  const raiseBig = () => (commit || spr < 1.5 ? allIn(v) : raiseTo(v, v.currentBet + (v.pot + v.toCall) * raiseFrac));
  const { fe: feR, base: baseR } = foldEquity(v, t, 0.9, true);

  if (adj > B.raiseThr - st * 0.25 && v.canRaise) {
    if (!raised && !river && tex < 0.5) return mix(0.3 * clamp(1 - st * 2, 0.3, 1.3), passive(v), raiseBig());
    return raiseBig();
  }
  if (hasDraw && hu && !raised && v.canRaise && !commit) {
    const f = bluffFreq(0.25, feR, baseR, 0.9) * clamp(1 - t.bluffy, 0.6, 1.4);
    if (rnd() < f) return { ...raiseBig(), freq: f };
  }
  if (adj > need) return passive(v);
  if (hasDraw && eq + 0.05 > odds) return passive(v);
  if (v.inPosition && hu && v.street === "flop" && betRatio <= 0.6 && !raised) {
    // Float more vs players who c-bet a lot and give up on the turn
    const r = t.raiser;
    const fl = B.float * (r ? clamp(1 + shift(dev(r, "cbet") * 2) + shift(-dev(r, "aggFreq")), 0.5, 1.8) : 1);
    return mix(fl, passive(v), fold(v));
  }
  if (river && hu && !raised && eq < 0.15 && v.canRaise) return mix(bluffFreq(0.06, feR, baseR, 1.2), raiseTo(v, v.currentBet * 2.5), fold(v));
  return fold(v);
}

// Same interface as the other profiles: view (public info + own cards) -> { type, amount?, freq? }. view.model is read-only.
export function proPlusPlusDecide(view) {
  const t = readTable(view);
  return view.street === "preflop" ? preflop(view, t) : postflop(view, t);
}
